
import { ArrowLeft, Camera, X, RefreshCw } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { uploadPerformanceData } from "../../services/apiService";
import { useState, useRef } from "react";
import { useGlobalState } from '../../utils/GlobalState';
import { getAuthToken, decodedToken } from "../../utils/auth";

const PerformanceRecording = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateState } = useGlobalState();
  const productData = location.state?.productData || {};
  const [whitePattern, setWhitePattern] = useState(null);
  const [blackPattern, setBlackPattern] = useState(null);
  const [whitePreview, setWhitePreview] = useState(null);
  const [blackPreview, setBlackPreview] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPreviewed, setHasPreviewed] = useState(false);
  const videoRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [currentCaptureType, setCurrentCaptureType] = useState(null);
  const [facingMode, setFacingMode] = useState("user");
  const [selectedImage, setSelectedImage] = useState(null);
  const decoded = decodedToken();
  const companyInfo = decoded?.companyInfo || 'Unknown'

  

  // Start camera with specified facing mode
  const startCamera = async (type) => {
    setCurrentCaptureType(type);
    setShowCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      alert(
        "Could not access camera. Please ensure camera permissions are granted."
      );
      setShowCamera(false);
    }
  };

  // Switch camera between front and rear
  const switchCamera = async () => {
    if (!videoRef.current || !showCamera) return;

    const newFacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newFacingMode);

    // Stop existing stream
    const currentStream = videoRef.current.srcObject;
    if (currentStream) {
      currentStream.getTracks().forEach((track) => track.stop());
    }

    // Start new stream with updated facing mode
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacingMode },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error switching camera:", error);
      alert("Could not switch camera.");
    }
  };

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);

    const imageUrl = canvas.toDataURL("image/jpeg");
    canvas.toBlob((blob) => {
      const file = new File([blob], `${currentCaptureType}_pattern.jpg`, {
        type: "image/jpeg",
      });
      if (currentCaptureType === "white") {
        setWhitePattern(file);
        setWhitePreview(imageUrl);
      } else {
        setBlackPattern(file);
        setBlackPreview(imageUrl);
      }
    }, "image/jpeg");

    // Stop camera
    const stream = video.srcObject;
    stream.getTracks().forEach((track) => track.stop());
    setShowCamera(false);
  };

  // Clear image
  const clearImage = (type) => {
    if (type === "white") {
      setWhitePattern(null);
      setWhitePreview(null);
    } else {
      setBlackPattern(null);
      setBlackPreview(null);
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (type === "white") {
      setWhitePattern(file);
      setWhitePreview(URL.createObjectURL(file));
    }
    if (type === "black") {
      setBlackPattern(file);
      setBlackPreview(URL.createObjectURL(file));
    }
  };

const handleSubmit = async () => {
  setIsSubmitting(true);
  try {
    // Validate authentication token
    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication token is missing. Please log in again.");
    }

    const decoded = decodedToken();
    if (!decoded || !decoded.email) {
      throw new Error("Invalid or expired token. Please log in again.");
    }

    const email = decoded.email;
    const companyInfo = decoded.companyInfo;

    // Validate required data
    if (!productData || !whitePattern || !blackPattern) {
      throw new Error("Missing required data: product info or pattern images.");
    }

    if (!productData.productType || !productData.modelName) {
      throw new Error("Product info must include productType and modelName.");
    }

    // Ensure screenType and companyInfo are included
    const formData = new FormData();
    formData.append("productInfo", JSON.stringify({
      ...productData,
      screenType: productData.productType === "MNT" ? productData.screenType : undefined,
      companyInfo: productData.companyInfo || companyInfo // Ensure companyInfo is included
    }));
    formData.append("email", email);
    formData.append("whitePattern", whitePattern);
    formData.append("blackPattern", blackPattern);

    await uploadPerformanceData(formData);

    // Clear the global state after successful submission
    updateState({ formData: [], uploadedImage: null });

    navigate("/performance-input");
  } catch (error) {
    console.error("Submission error:", error);
    alert(error.message);
    if (error.message.includes("token")) {
      navigate("/login", { state: { error: error.message } });
    }
  } finally {
    setIsSubmitting(false);
  }
};

  const handleBack = () => {
    // Navigate back to ProductInfoInput and pass the productData
    navigate(-1, { state: { productData } });
  };

  return (
    <div className="min-h-screen bg-black text-white ">
      <div className="max-w-4xl mx-auto p-4">
        <div className="flex items-center mb-6">
          <button
            className="mr-4 hover:opacity-80"
            onClick={handleBack}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-medium">Good Performance Recording</h1>
        </div>

        {/* Enhanced Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 rounded-xl shadow-2xl max-w-3xl w-full overflow-hidden">
              <div className="relative">
                <video
                  ref={videoRef}
                  autoPlay
                  className="w-full max-h-[70vh] object-contain rounded-t-xl"
                />
                {/* Controls */}
                <div className="flex justify-between items-center p-4 bg-gray-800 rounded-b-xl">
                  <button
                    onClick={switchCamera}
                    className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
                    title={`Switch to ${
                      facingMode === "user" ? "rear" : "front"
                    } camera`}
                  >
                    <RefreshCw className="w-6 h-6" />
                  </button>
                  <button
                    onClick={capturePhoto}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-full transition-colors"
                  >
                    Capture
                  </button>
                  <button
                    onClick={() => {
                      const stream = videoRef.current.srcObject;
                      stream.getTracks().forEach((track) => track.stop());
                      setShowCamera(false);
                    }}
                    className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-8 max-w-2xl mx-auto">
          {/* White Pattern */}
          <div>
            <h2 className="text-lg mb-3">White Pattern</h2>
            <div className="w-full aspect-video bg-white border-2 border-blue-600 rounded-lg mb-2 overflow-hidden">
              {whitePreview && (
                <img
                  src={whitePreview}
                  alt="White pattern preview"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="flex items-center justify-between text-gray-400 text-sm">
              <span>
                {whitePattern ? whitePattern.name : "Capture photo of TV"}
              </span>
              <span className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "white")}
                  className="hidden"
                  id="white-upload"
                />
                <label
                  htmlFor="white-upload"
                  className="cursor-pointer hover:text-blue-400 transition-colors"
                >
                  <span>Upload</span>
                </label>
                <button
                  onClick={() => startCamera("white")}
                  className="p-1 hover:text-blue-400 transition-colors"
                >
                  <Camera className="w-5 h-5" />
                </button>
                {whitePattern && (
                  <button
                    onClick={() => clearImage("white")}
                    className="p-1 hover:text-red-400 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </span>
            </div>
          </div>

          {/* Black Pattern */}
          <div>
            <h2 className="text-lg mb-3">Black Pattern</h2>
            <div className="w-full aspect-video bg-black border-2 border-blue-600 rounded-lg mb-2 overflow-hidden">
              {blackPreview && (
                <img
                  src={blackPreview}
                  alt="Black pattern preview"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="flex items-center justify-between text-gray-400 text-sm">
              <span>
                {blackPattern ? blackPattern.name : "Capture photo of TV"}
              </span>
              <span className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, "black")}
                  className="hidden"
                  id="black-upload"
                />
                <label
                  htmlFor="black-upload"
                  className="cursor-pointer hover:text-blue-400 transition-colors"
                >
                  <span>Upload</span>
                </label>
                <button
                  onClick={() => startCamera("black")}
                  className="p-1 hover:text-blue-400 transition-colors"
                >
                  <Camera className="w-5 h-5" />
                </button>
                {blackPattern && (
                  <button
                    onClick={() => clearImage("black")}
                    className="p-1 hover:text-red-400 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </span>
            </div>
          </div>
          <div>
            <button
              className="w-full py-3 rounded-lg mt-4 text-white transition-colors bg-green-600 hover:bg-green-700"
              onClick={() => {
                setShowPreviewModal(true);
                setHasPreviewed(true);
              }}
            >
              Preview
            </button>
            <button 
              className={`w-full py-3 rounded-lg mt-8 text-white transition-colors ${
                !whitePattern || !blackPattern || isSubmitting 
                  ? 'bg-gray-600 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
              onClick={handleSubmit}
              disabled={!whitePattern || !blackPattern || isSubmitting}
            >
              {isSubmitting ? (
                <span className="submit-loader"></span>
              ) : (
                'Submit'
              )}
            </button>

            {showPreviewModal && (
              <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
                <div className="bg-gray-900 rounded-xl shadow-2xl max-w-3xl w-full overflow-hidden p-4 max-h-[90vh] overflow-y-auto">
                  <h2 className="text-lg text-white text-center mb-4">
                    Preview Submission
                  </h2>

                  {/* Product Details */}
                  <div className="text-white p-4 border-b border-gray-700">
                    <h3 className="text-lg font-semibold">
                      {productData.productType}
                    </h3>
                    <p className="text-sm text-gray-400">
                      Model: {productData.modelName}
                    </p>
                    {productData.bezel && (
                      <p className="text-sm text-gray-400">
                        Bezel: {productData.bezel}
                      </p>
                    )}
                    {/* <p className="text-sm text-gray-400">
                      Leakage Ratio: {productData.lightLeakageRatioCriteria}
                    </p> */}
                    <p className="text-sm text-gray-400">
                      Dimensions: {productData.dimensions?.width} x{" "}
                      {productData.dimensions?.height}
                    </p>
                    {productData.productType === "MNT" && productData.screenType && (
                      <p className="text-sm text-gray-400">
                        Screen Type: {productData.screenType}
                      </p>
                    )}
                    {productData.inchSize && (
                      <p className="text-sm text-gray-400">
                        Inch Size: {productData.inchSize}"
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <h3 className="text-sm text-gray-400 text-center">
                        White Pattern
                      </h3>
                      <div
                        className="w-full h-64 flex items-center justify-center border-2 border-dashed border-gray-400 rounded-lg overflow-hidden cursor-pointer"
                        onClick={() =>
                          whitePreview && setSelectedImage(whitePreview)
                        }
                      >
                        {whitePreview ? (
                          <img
                            src={whitePreview}
                            alt="White Pattern Preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-500">
                            Please upload the image
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm text-gray-400 text-center">
                        Black Pattern
                      </h3>
                      <div
                        className="w-full h-64 flex items-center justify-center border-2 border-dashed border-gray-400 rounded-lg overflow-hidden cursor-pointer"
                        onClick={() =>
                          blackPreview && setSelectedImage(blackPreview)
                        }
                      >
                        {blackPreview ? (
                          <img
                            src={blackPreview}
                            alt="Black Pattern Preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-500">
                            Please upload the image
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Full-Screen Modal */}
                  {selectedImage && (
                    <div
                      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
                      onClick={() => setSelectedImage(null)}
                    >
                      {/* Close Button */}
                      <button
                        className="absolute top-2 right-2 p-1 sm:p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
                        onClick={() => setSelectedImage(null)}
                      >
                        <X className="w-4 h-4 sm:w-6 sm:h-6" />
                      </button>
                      <img
                        src={selectedImage}
                        alt="Full Preview"
                        className="object-contain"
                        width={900}
                        height={900}
                      />
                    </div>
                  )}
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="absolute top-2 right-2 p-1 sm:p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 sm:w-6 sm:h-6" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceRecording;

